import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const CartContext = createContext(null);

const cartKey = (id, size, color, customOptions) => {
  const opts = customOptions ? Object.entries(customOptions).sort().map(([k, v]) => `${k}:${v}`).join("|") : "";
  return `${id}-${size || ""}-${color || ""}-${opts}`;
};

export function CartProvider({ children }) {
  const { t } = useLanguage();
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rebelky_cart") || "[]");
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("rebelky_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1, size = null, color = null, customOptions = null) => {
    setItems(prev => {
      const key = cartKey(product._id, size, color, customOptions);
      const existing = prev.find(i => cartKey(i._id, i.size, i.color, i.custom_options) === key);
      if (existing) {
        return prev.map(i => cartKey(i._id, i.size, i.color, i.custom_options) === key
          ? { ...i, quantity: i.quantity + qty }
          : i);
      }
      return [...prev, { ...product, quantity: qty, size, color, custom_options: customOptions }];
    });
    const detail = [
      size ? `${t.cart.sizeShort} ${size}` : null,
      color,
      customOptions ? Object.entries(customOptions).map(([k, v]) => `${k}: ${v}`).join(", ") : null,
    ].filter(Boolean).join(" · ");
    toast.success(`${t.cart.addedToast}: ${product.name}`, {
      description: detail || `${qty}× ${(product.price * qty).toFixed(0)} Kč`,
      duration: 2500,
    });
  };

  const removeItem = (productId, size, color, customOptions = null) => {
    setItems(prev => prev.filter(i => cartKey(i._id, i.size, i.color, i.custom_options) !== cartKey(productId, size, color, customOptions)));
  };

  const updateQty = (productId, size, color, qty, customOptions = null) => {
    if (qty < 1) { removeItem(productId, size, color, customOptions); return; }
    setItems(prev => prev.map(i =>
      cartKey(i._id, i.size, i.color, i.custom_options) === cartKey(productId, size, color, customOptions)
        ? { ...i, quantity: qty } : i,
    ));
  };

  const clearCart = () => setItems([]);

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
